import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { BedDouble, Bath, Maximize } from 'lucide-react';
import { Button } from './ui/Button';
import { Card, CardContent } from './ui/Card';
export function FloorPlansSection() {
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 40
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.7,
        ease: 'easeOut'
      }
    }
  };
  return <section id="floor-plans" className="py-24 bg-white" data-id="element-42">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-43">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-12" data-id="element-44">
          <div className="max-w-2xl" data-id="element-45">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-46">
              Find Your Perfect Floor Plan
            </h2>
            <p className="text-lg text-charcoal/70" data-id="element-47">
              Discover our signature layouts, designed for modern living with
              premium finishes standard.
            </p>
          </div>
          <div className="mt-6 md:mt-0" data-id="element-48">
            <Button variant="ghost" className="hidden md:inline-flex" data-id="element-49">
              View All Plans &rarr;
            </Button>
          </div>
        </div>

        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-50">
          {/* Plan 1 */}
          <motion.div variants={itemVariants} data-id="element-51">
            <Card className="h-full flex flex-col border-cream bg-cream/30 hover:border-gold/50 transition-colors duration-300" data-id="element-52">
              {/* Placeholder Image Area */}
              <div className="h-64 bg-forest/5 relative flex items-center justify-center p-6 border-b border-forest/10" data-id="element-53">
                <div className="absolute inset-0 opacity-10 bg-[radial-gradient(#1B3A2D_1px,transparent_1px)] [background-size:16px_16px]" data-id="element-54"></div>
                <div className="relative w-full h-full border-2 border-forest/20 flex items-center justify-center" data-id="element-55">
                  <span className="font-serif text-forest/40 text-xl tracking-widest uppercase" data-id="element-56">
                    The Cascade
                  </span>
                </div>
              </div>

              <CardContent className="flex-1 flex flex-col p-6" data-id="element-57">
                <div className="flex justify-between items-start mb-4" data-id="element-58">
                  <h3 className="text-2xl font-bold text-forest" data-id="element-59">
                    The Cascade
                  </h3>
                  <span className="text-sm font-semibold text-gold bg-gold/10 px-2 py-1 rounded" data-id="element-60">
                    From $750k
                  </span>
                </div>

                <div className="flex space-x-4 mb-6 text-sm text-charcoal/70" data-id="element-61">
                  <div className="flex items-center" data-id="element-62">
                    <Maximize size={16} className="mr-1.5 text-forest/60" data-id="element-63" />{' '}
                    2,400 sq ft
                  </div>
                  <div className="flex items-center" data-id="element-64">
                    <BedDouble size={16} className="mr-1.5 text-forest/60" data-id="element-65" /> 3
                    Bed
                  </div>
                  <div className="flex items-center" data-id="element-66">
                    <Bath size={16} className="mr-1.5 text-forest/60" data-id="element-67" /> 2.5
                    Bath
                  </div>
                </div>

                <p className="text-charcoal/80 mb-8 flex-1" data-id="element-68">
                  A brilliant open-concept design featuring a stunning great
                  room, gourmet kitchen, and a private primary suite retreat.
                </p>

                <Button to="/experience" variant="secondary" fullWidth className="mt-auto" data-id="element-69">
                  Customize This Home
                </Button>
              </CardContent>
            </Card>
          </motion.div>

          {/* Plan 2 */}
          <motion.div variants={itemVariants} data-id="element-70">
            <Card className="h-full flex flex-col border-cream bg-cream/30 hover:border-gold/50 transition-colors duration-300 relative" data-id="element-71">
              <div className="absolute top-4 right-4 z-10 bg-gold text-forest text-xs font-bold px-3 py-1 uppercase tracking-wider shadow-sm" data-id="element-72">
                Most Popular
              </div>

              {/* Placeholder Image Area */}
              <div className="h-64 bg-forest/5 relative flex items-center justify-center p-6 border-b border-forest/10" data-id="element-73">
                <div className="absolute inset-0 opacity-10 bg-[radial-gradient(#1B3A2D_1px,transparent_1px)] [background-size:16px_16px]" data-id="element-74"></div>
                <div className="relative w-full h-full border-2 border-forest/20 flex items-center justify-center" data-id="element-75">
                  <span className="font-serif text-forest/40 text-xl tracking-widest uppercase" data-id="element-76">
                    The Olympic
                  </span>
                </div>
              </div>

              <CardContent className="flex-1 flex flex-col p-6" data-id="element-77">
                <div className="flex justify-between items-start mb-4" data-id="element-78">
                  <h3 className="text-2xl font-bold text-forest" data-id="element-79">
                    The Olympic
                  </h3>
                  <span className="text-sm font-semibold text-gold bg-gold/10 px-2 py-1 rounded" data-id="element-80">
                    From $890k
                  </span>
                </div>

                <div className="flex space-x-4 mb-6 text-sm text-charcoal/70" data-id="element-81">
                  <div className="flex items-center" data-id="element-82">
                    <Maximize size={16} className="mr-1.5 text-forest/60" data-id="element-83" />{' '}
                    3,100 sq ft
                  </div>
                  <div className="flex items-center" data-id="element-84">
                    <BedDouble size={16} className="mr-1.5 text-forest/60" data-id="element-85" /> 4
                    Bed
                  </div>
                  <div className="flex items-center" data-id="element-86">
                    <Bath size={16} className="mr-1.5 text-forest/60" data-id="element-87" /> 3 Bath
                  </div>
                </div>

                <p className="text-charcoal/80 mb-8 flex-1" data-id="element-88">
                  Designed for growing families, offering expansive living
                  areas, a dedicated home office, and a covered outdoor living
                  space.
                </p>

                <Button to="/experience" variant="primary" fullWidth className="mt-auto" data-id="element-89">
                  Customize This Home
                </Button>
              </CardContent>
            </Card>
          </motion.div>

          {/* Plan 3 */}
          <motion.div variants={itemVariants} data-id="element-90">
            <Card className="h-full flex flex-col border-cream bg-cream/30 hover:border-gold/50 transition-colors duration-300" data-id="element-91">
              {/* Placeholder Image Area */}
              <div className="h-64 bg-forest/5 relative flex items-center justify-center p-6 border-b border-forest/10" data-id="element-92">
                <div className="absolute inset-0 opacity-10 bg-[radial-gradient(#1B3A2D_1px,transparent_1px)] [background-size:16px_16px]" data-id="element-93"></div>
                <div className="relative w-full h-full border-2 border-forest/20 flex items-center justify-center" data-id="element-94">
                  <span className="font-serif text-forest/40 text-xl tracking-widest uppercase" data-id="element-95">
                    The Rainier
                  </span>
                </div>
              </div>

              <CardContent className="flex-1 flex flex-col p-6" data-id="element-96">
                <div className="flex justify-between items-start mb-4" data-id="element-97">
                  <h3 className="text-2xl font-bold text-forest" data-id="element-98">
                    The Rainier
                  </h3>
                  <span className="text-sm font-semibold text-gold bg-gold/10 px-2 py-1 rounded" data-id="element-99">
                    From $1.1M
                  </span>
                </div>

                <div className="flex space-x-4 mb-6 text-sm text-charcoal/70" data-id="element-100">
                  <div className="flex items-center" data-id="element-101">
                    <Maximize size={16} className="mr-1.5 text-forest/60" data-id="element-102" />{' '}
                    3,800 sq ft
                  </div>
                  <div className="flex items-center" data-id="element-103">
                    <BedDouble size={16} className="mr-1.5 text-forest/60" data-id="element-104" /> 5
                    Bed
                  </div>
                  <div className="flex items-center" data-id="element-105">
                    <Bath size={16} className="mr-1.5 text-forest/60" data-id="element-106" /> 4 Bath
                  </div>
                </div>

                <p className="text-charcoal/80 mb-8 flex-1" data-id="element-107">
                  Our most luxurious estate plan. Features a grand two-story
                  foyer, chef's kitchen with prep pantry, and a main-floor guest
                  suite.
                </p>

                <Button to="/experience" variant="secondary" fullWidth className="mt-auto" data-id="element-108">
                  Customize This Home
                </Button>
              </CardContent>
            </Card>
          </motion.div>
        </motion.div>

        <div className="mt-10 text-center md:hidden" data-id="element-109">
          <Button variant="outline" fullWidth data-id="element-110">
            View All Plans
          </Button>
        </div>
      </div>
    </section>;
}